import React, { useState, useEffect } from 'react';
import { Upload, Download, FileSpreadsheet, Calendar, MapPin, User, Phone, Package, DollarSign, RefreshCw, CheckCircle, AlertCircle, X, Check, ChevronDown, Printer } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import MainService from '../services/mainservice';
import * as XLSX from 'xlsx';
import { getConfig } from '../services/config';

const UploadOrders = () => {
  const [orders, setOrders] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [emirates, setEmirates] = useState([]);
  const [companyProfile, setCompanyProfile] = useState({});
  const [selectedClient, setSelectedClient] = useState({
    MobileNo1: '',
    Id: '',
    Name: '',
    AddressId: ''
  });
  const [batchNo, setBatchNo] = useState('');
  const [filters, setFilters] = useState({
    DeliveryDate: new Date(),
    PickupDate: new Date()
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [showPrintDropdown, setShowPrintDropdown] = useState(false);
  const { showSuccess, showError, showWarning } = useToast();

  useEffect(() => {
    initializeData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPrintDropdown && !event.target.closest('.relative')) {
        setShowPrintDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showPrintDropdown]);

  const initializeData = async () => {
    try {
      setIsLoading(true);
      
      // Get token info from localStorage
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (tokenData) {
        const userData = JSON.parse(tokenData);
        const clientData = {
          MobileNo1: userData.MobileNo1,
          Id: userData.ClientId,
          Name: userData.Name,
          Token : userData.Token,
          AddressId: ''
        };
        setSelectedClient(clientData);

        // Load addresses after setting client
        try {
          const addressesResponse = await MainService.GetClientAddress(clientData.Id);
          setAddresses(addressesResponse || []);
        } catch (error) {
          console.error('Error loading addresses:', error);
          setAddresses([]);
        }
      }

      // Load emirates
      try {
        const emiratesResponse = await MainService.EmiratesInDropDown();
        setEmirates(emiratesResponse || []);
      } catch (error) {
        console.error('Error loading emirates:', error);
        setEmirates([]);
      }

      // Load batch number
      try {
        const batchResponse = await MainService.GetNextBatchNo();
        setBatchNo(batchResponse || '');
      } catch (error) {
        console.error('Error loading batch number:', error);
        setBatchNo('');
      }

      // Load company profile
      try {
        const profileResponse = await MainService.GetCompanyProfile();
        setCompanyProfile(profileResponse.data || {});

        // Set dates based on company profile
        if (profileResponse.data && !profileResponse.data.IsSameDay) {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          setFilters(prev => ({
            ...prev,
            PickupDate: new Date(),
            DeliveryDate: tomorrow
          }));
        }
      } catch (error) {
        console.error('Error loading company profile:', error);
        setCompanyProfile({});
      }

    } catch (error) {
      console.error('Error initializing data:', error);
      showError('Failed to load initial data. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  };

  const processFile = (file) => {
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      showError('Please select a valid Excel file (.xlsx or .xls)');
      return;
    }

    // Check file size (limit to 10MB)
    if (file.size > 10 * 1024 * 1024) {
      showError('File size too large. Please select a file smaller than 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          showError('No data found in the Excel file.');
          return;
        }

        // Validate required columns
        const requiredColumns = ['Customer', 'MobileNo', 'MobileNo2', 'Emirate', 'Location', 'Address', 'Quantity', 'ProductPrice'];
        const missingColumns = requiredColumns.filter(col => !jsonData[0].hasOwnProperty(col));
        
        if (missingColumns.length > 0) {
          showError(`Missing required columns: ${missingColumns.join(', ')}. Please use the template.`);
          return;
        }

        // Process the data similar to the Angular version
        const processedOrders = jsonData.map((row, index) => {
          const order = {
            id: index + 1,
            VoucherNo: row.VoucherNo || '',
            ClientBillNo: row.ClientBillNo || '',
            ChargeFrom: companyProfile.DefChargeFrom || 'CLIENT',
            PayType: row.PayType || 'COD',
            CustomerName: row.Customer || '',
            MobileNo: row.MobileNo || '',
            MobileNo2: row.MobileNo2 || '',
            Emirate: row.Emirate || '',
            EmirateId: '',
            Location: row.Location || '', 
            Address: row.Address || '',
            PickupDate: filters.PickupDate,
            DeliveryDate: filters.DeliveryDate,
            Quantity: parseFloat(row.Quantity) || 0,
            ProductPrice: parseFloat(row.ProductPrice) || 0,
            DeliveryCharge: 0,
            TempServiceCharge: 0,
            TAX: 0,
            CustomerAmount: 0,
            ClientAmount: 0,
            Remarks: row.DeliveryInstructions || '',
            BatchNo: batchNo,
            StoreRefNo: 0,
            IsAdvancePrice: false,
            IsAdvancePay: false
          };

          // Check emirate similarity
          checkEmirate(order);
          return order;
        });

        setOrders(processedOrders);
        showSuccess(`Successfully loaded ${processedOrders.length} orders from Excel file`);
      } catch (error) {
        console.error('Error parsing Excel file:', error);
        showError('Error parsing Excel file. Please check the format and try again.');
      }
    };

    reader.onerror = () => {
      showError('Error reading file. Please try again.');
    };

    reader.readAsBinaryString(file);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    
    if (!selectedClient.AddressId) {
      showWarning('Please select an address first');
      return;
    }

    const files = event.dataTransfer.files;
    if (files && files[0]) {
      processFile(files[0]);
    }
  };

  const checkEmirate = (order) => {
    let bestMatch = null;
    let bestSimilarity = 0;

    emirates.forEach(emirate => {
      const similarity = calculateSimilarity(emirate.Name, order.Emirate);
      if (similarity > bestSimilarity) {
        bestMatch = emirate;
        bestSimilarity = similarity;
      }
    });

    if (bestMatch) {
      order.EmirateId = bestMatch.Id;
    }
  };

  const calculateSimilarity = (s1, s2) => {
    const longer = s1.length > s2.length ? s1 : s2;
    const shorter = s1.length > s2.length ? s2 : s1;
    const longerLength = longer.length;
    
    if (longerLength === 0) return 1.0;
    
    const editDistance = getEditDistance(longer, shorter);
    return (longerLength - editDistance) / longerLength;
  };

  const getEditDistance = (s1, s2) => {
    s1 = s1.toLowerCase();
    s2 = s2.toLowerCase();
    
    const costs = [];
    for (let i = 0; i <= s1.length; i++) {
      let lastValue = i;
      for (let j = 0; j <= s2.length; j++) {
        if (i === 0) {
          costs[j] = j;
        } else if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
      if (i > 0) costs[s2.length] = lastValue;
    }
    return costs[s2.length];
  };

  const handleOrderChange = (index, field, value) => {
    const updatedOrders = [...orders];
    updatedOrders[index] = { ...updatedOrders[index], [field]: value };
    setOrders(updatedOrders);
  };

  const applyDateToAll = () => {
    const updatedOrders = orders.map(order => ({
      ...order,
      PickupDate: filters.PickupDate,
      DeliveryDate: filters.DeliveryDate
    }));
    setOrders(updatedOrders);
    showSuccess('Dates applied to all orders');
  };
 

  const uploadOrders = async (temp) => {
    if (!selectedClient.AddressId) {
      showWarning('Please select your address');
      return;
    }

    if (orders.length === 0) {
      showWarning('No orders to upload. Please upload an Excel file first.');
      return;
    }

    const invalidOrders = orders.filter(order => 
      !order.EmirateId || !order.CustomerName || 
      !order.MobileNo || !order.MobileNo2 || !['COD', 'CAD'].includes(order.PayType)
    );

    if (invalidOrders.length > 0) {
      showWarning('Invalid Format: CAD/COD, Emirate, Location, Customer, Mobile No 1, and Mobile No 2 are required');
      return;
    }

    try {
      setIsUploading(true);
      
      const ordersToUpload = orders.map(order => ({
        ...order,
        PMonth: order.PickupDate.getMonth() + 1,
        PDay: order.PickupDate.getDate(),
        PYear: order.PickupDate.getFullYear(),
        DMonth: order.DeliveryDate.getMonth() + 1,
        DDay: order.DeliveryDate.getDate(),
        DYear: order.DeliveryDate.getFullYear(),
        ClientId: selectedClient.Id,
        Client: selectedClient,
        ClientAddressId: selectedClient.AddressId,
        IsPickup: true
      }));

      const response = await MainService.SavePortalOrderList(ordersToUpload, selectedClient.Token);
      
      if (response.Status === 'Success') {
        showSuccess('Orders uploaded successfully!');
        setOrders([]);
        initializeData();
        if(temp == 1){
          await printOrders(1,response.OrderIds);
          showSuccess('Printing!');
        }
        if(temp == 2){
          await printOrders(2, response.OrderIds); 
          showSuccess('Printing!');
        }
        if(temp == 0){
          setIsUploading(false);
        }
      } else {
        showError(response.Status || 'Failed to upload orders');
        setIsUploading(false);
      }
    } catch (error) {
      console.error('Error uploading orders:', error);
      showError('Network error. Please try again.');
      setIsUploading(false);
    }
  };

  const printOrders = async (printType, orderIds) => {
    try {
      setShowPrintDropdown(false);
      
      let response;
      if (printType === 1) {
        response = await MainService.PrintOrderList(orderIds);
      } else {
        response = await MainService.PrintOrderList2(orderIds);
      }
      
      if (response && response.includes('.pdf')) {
        // Open the print file in a new window
        const printUrl = `${getConfig().API_BASE_URL}/ReportFiles/${response}`;
        window.open(printUrl, '_blank');
        showSuccess(`Print ${printType} generated successfully!`);
      } else {
        showError('Print generation failed - Invalid file format');
      }
    } catch (error) {
      console.error('Print error:', error);
      showError('Failed to generate print. Please try again.');
    } finally {
      setIsUploading(false);
      setShowPrintDropdown(false);
    }
  };

  const downloadTemplate = () => {
    try {
      // Create a link to download the existing template file
      const link = document.createElement('a');
      link.href = '/docs/BulkOrder.xlsx';
      link.download = 'BulkOrder.xlsx';
      link.target = '_blank';
      
      // Append to body, click, and remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess('Template downloaded successfully!');
    } catch (error) {
      console.error('Error downloading template:', error);
      showError('Failed to download template. Please try again.');
    }
  };

  if (isLoading) {
    return (
      <div className="page-container flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-primary-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Bulk Order Entry</h1>
              <p className="text-gray-600">Upload Excel file to create multiple orders at once</p>
              {batchNo && (
                <p className="text-sm text-primary-600 font-medium">Batch #{batchNo}</p>
              )}
            </div>
            <div className="flex flex-shrink-0 flex-col sm:flex-row gap-3">
              <button
                onClick={downloadTemplate}
                className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-medium py-2 px-4 rounded-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Template</span>
              </button>
              
              <button
                onClick={() => {
                  setOrders([]);
                  initializeData();
                }}
                className="btn-secondary flex items-center justify-center space-x-2"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Refresh</span>
              </button>
              
              <div className="relative">
                <button
                  onClick={() => setShowPrintDropdown(!showPrintDropdown)}
                  disabled={isUploading || orders.length === 0 || !selectedClient.AddressId}
                  className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUploading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      <span>Processing...</span>
                    </>
                  ) : (
                    <>
                      <Printer className="w-4 h-4" />
                      <span>Upload & Print</span>
                      <ChevronDown className="w-4 h-4" />
                    </>
                  )}
                </button>
                
                {showPrintDropdown && !isUploading && (
                  <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
                    <button
                      onClick={() => uploadOrders(0)}
                      className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Upload Onnly</span>
                    </button>  
                    <button
                      onClick={() => uploadOrders(1)}
                      className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Upload and Print 1</span>
                    </button>
                    <button
                      onClick={() => uploadOrders(2)}
                      className="w-full px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Upload and Print 2</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Upload Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Address Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Pickup Address
              </label>
              <select
                value={selectedClient.AddressId}
                onChange={(e) => setSelectedClient(prev => ({ ...prev, AddressId: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                disabled={addresses.length === 0}
              >
                <option value="">
                  {addresses.length === 0 ? 'No addresses available' : 'Select Address'}
                </option>
                {addresses.map(address => (
                  <option key={address.Id} value={address.Id}>
                    {address.Address} - {address.Location?.Name} - {address.Emirate?.Name}
                  </option>
                ))}
              </select>
              {addresses.length === 0 && (
                <p className="text-xs text-red-500 mt-1">Please contact support to add addresses</p>
              )}
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose Excel File
              </label>
              <div className="relative">
                <div className="flex items-center justify-center w-full">
                  <label 
                    htmlFor="file-upload" 
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-colors duration-200 ${
                      !selectedClient.AddressId 
                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
                        : 'border-gray-300 bg-gray-50 hover:bg-gray-100 hover:border-primary-400'
                    }`}
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className={`w-8 h-8 mb-2 ${!selectedClient.AddressId ? 'text-gray-300' : 'text-gray-400'}`} />
                      <p className={`text-sm ${!selectedClient.AddressId ? 'text-gray-400' : 'text-gray-500'}`}>
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className={`text-xs ${!selectedClient.AddressId ? 'text-gray-400' : 'text-gray-500'}`}>
                        Excel files (.xlsx, .xls)
                      </p>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={handleFileUpload}
                      disabled={!selectedClient.AddressId}
                      className="hidden"
                    />
                  </label>
                </div>
                {!selectedClient.AddressId && (
                  <p className="text-xs text-red-500 mt-1">Please select an address first</p>
                )}
              </div>
            </div>
          </div>

        </div>

        {/* Orders Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-200">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Orders ({orders.length})
              </h2>
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Pickup Date
                    </label>
                    <input
                      type="date"
                      value={filters.PickupDate.toISOString().split('T')[0]}
                      onChange={(e) => setFilters(prev => ({ ...prev, PickupDate: new Date(e.target.value) }))}
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Delivery Date
                    </label>
                    <input
                      type="date"
                      value={filters.DeliveryDate.toISOString().split('T')[0]}
                      onChange={(e) => setFilters(prev => ({ ...prev, DeliveryDate: new Date(e.target.value) }))}
                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                  </div>
                </div>
                <button
                  onClick={applyDateToAll}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium py-2 px-3 rounded-lg transition-all duration-200 flex items-center justify-center mt-5"
                  title="Apply Date"
                >
                  <Check className="w-4 h-4" />
                </button>
                <div className="text-center p-3 bg-green-50 rounded-lg text-sm">
                  <p className="text-gray-500 text-xs">Total Product Price</p>
                  <p className="text-lg font-semibold text-green-600">
                    AED {orders.reduce((sum, order) => sum + (order.ProductPrice || 0), 0).toFixed(2)}
                  </p>
                </div>
              </div>
            </div>
          </div>
            
            <div className="overflow-x-auto border border-gray-300 m-2">
              <table className="w-full border-collapse">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-1 py-2 text-center text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '50px'}}>#</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Voucher No</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Client Bill No</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Charge From</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Pay Type</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Customer</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Mobile No 1</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Mobile No 2</th>
                     <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '125px'}}>Emirate</th>
                     <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '125px'}}>Location</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '200px'}}>Address</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Pickup Date</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Delivery Date</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Quantity</th>
                    <th className="border border-gray-300 px-1 py-2 text-left text-xs font-medium text-gray-700 bg-gray-200" style={{minWidth: '100px'}}>Product Price</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {orders.length > 0 ? (
                    orders.map((order, index) => (
                      <tr key={order.id} className="hover:bg-blue-50">
                        <td className="border border-gray-300 px-1 py-1 text-sm text-gray-900 bg-gray-50 text-center" style={{minWidth: '50px'}}>{index + 1}</td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="text"
                            value={order.VoucherNo}
                            onChange={(e) => handleOrderChange(index, 'VoucherNo', e.target.value)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="text"
                            value={order.ClientBillNo}
                            onChange={(e) => handleOrderChange(index, 'ClientBillNo', e.target.value)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <select
                            value={order.ChargeFrom}
                            onChange={(e) => handleOrderChange(index, 'ChargeFrom', e.target.value)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          >
                            <option value="CLIENT">Client</option>
                            <option value="AREA">Area</option>
                          </select>
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <select
                            value={order.PayType}
                            onChange={(e) => handleOrderChange(index, 'PayType', e.target.value)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          >
                            <option value="CAD">CAD</option>
                            <option value="COD">COD</option>
                          </select>
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="text"
                            value={order.CustomerName}
                            onChange={(e) => handleOrderChange(index, 'CustomerName', e.target.value)}
                            className={`w-full h-8 px-1 py-0 border-0 focus:outline-none text-sm ${
                              !order.CustomerName ? 'bg-red-100 text-red-700' : 'focus:bg-blue-100'
                            }`}
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="text"
                            value={order.MobileNo}
                            onChange={(e) => handleOrderChange(index, 'MobileNo', e.target.value)}
                            className={`w-full h-8 px-1 py-0 border-0 focus:outline-none text-sm ${
                              !order.MobileNo ? 'bg-red-100 text-red-700' : 'focus:bg-blue-100'
                            }`}
                            required
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="text"
                            value={order.MobileNo2}
                            onChange={(e) => handleOrderChange(index, 'MobileNo2', e.target.value)}
                            className={`w-full h-8 px-1 py-0 border-0 focus:outline-none text-sm ${
                              !order.MobileNo2 ? 'bg-red-100 text-red-700' : 'focus:bg-blue-100'
                            }`}
                            required
                          />
                        </td>
                         <td className="border border-gray-300 px-0 py-0" style={{minWidth: '125px'}}>
                           <select
                             value={order.EmirateId}
                             onChange={(e) => handleOrderChange(index, 'EmirateId', e.target.value)}
                             className={`w-full h-8 px-1 py-0 border-0 focus:outline-none text-sm ${
                               !order.EmirateId ? 'bg-red-100 text-red-700' : 'focus:bg-blue-100'
                             }`}
                           >
                             <option value="">Select Emirate</option>
                             {emirates.map(emirate => (
                               <option key={emirate.Id} value={emirate.Id}>
                                 {emirate.Name}
                               </option>
                             ))}
                           </select>
                         </td>
                         <td className="border border-gray-300 px-0 py-0" style={{minWidth: '125px'}}>
                           <input
                             type="text"
                             value={order.Location}
                             onChange={(e) => handleOrderChange(index, 'Location', e.target.value)}
                             className={`w-full h-8 px-1 py-0 border-0 focus:outline-none text-sm ${
                               !order.Location ? 'bg-red-100 text-red-700' : 'focus:bg-blue-100'
                             }`}
                             placeholder="Enter location"
                           />
                         </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '200px'}}>
                          <textarea
                            value={order.Address}
                            onChange={(e) => handleOrderChange(index, 'Address', e.target.value)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm resize-none"
                            rows="1"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="date"
                            value={order.PickupDate.toISOString().split('T')[0]}
                            onChange={(e) => handleOrderChange(index, 'PickupDate', new Date(e.target.value))}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="date"
                            value={order.DeliveryDate.toISOString().split('T')[0]}
                            onChange={(e) => handleOrderChange(index, 'DeliveryDate', new Date(e.target.value))}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="number"
                            value={order.Quantity}
                            onChange={(e) => handleOrderChange(index, 'Quantity', parseFloat(e.target.value) || 0)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm text-right"
                          />
                        </td>
                        <td className="border border-gray-300 px-0 py-0" style={{minWidth: '100px'}}>
                          <input
                            type="number"
                            value={order.ProductPrice}
                            onChange={(e) => handleOrderChange(index, 'ProductPrice', parseFloat(e.target.value) || 0)}
                            className="w-full h-8 px-1 py-0 border-0 focus:outline-none focus:bg-blue-100 text-sm text-right"
                          />
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="15" className="border border-gray-300 px-1 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center space-y-2">
                          <Package className="w-12 h-12 text-gray-300" />
                          <p className="text-lg font-medium">No orders loaded</p>
                          <p className="text-sm">Upload an Excel file to see orders here</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

      </div>
    </div>
  );
};

export default UploadOrders;
